import { useState, useEffect, useRef, useCallback } from "react";

const API = window.location.origin;
const CORRECT_PASSWORD = "Saumya@123";

// ── Helpers ──────────────────────────────────────────────────────
const statusColor = (s) => {
  if (s === "running") return "#06b6d4";
  if (s === "completed") return "#10b981";
  if (s === "stopped") return "#ef4444";
  if (s === "paused") return "#f59e0b";
  return "#64748b";
};
const pct = (c, t) => (t > 0 ? Math.round((c / t) * 100) : 0);

// ── Password Gate ─────────────────────────────────────────────────
function PasswordGate({ onAuth }) {
  const [val, setVal] = useState("");
  const [err, setErr] = useState(false);
  const [shake, setShake] = useState(false);

  const submit = () => {
    if (val === CORRECT_PASSWORD) {
      sessionStorage.setItem("qb_admin_authed", "1");
      onAuth();
    } else {
      setErr(true);
      setShake(true);
      setTimeout(() => setShake(false), 500);
    }
  };

  return (
    <div style={{
      minHeight: "100vh", background: "#080e1a",
      display: "flex", alignItems: "center", justifyContent: "center"
    }}>
      <div
        data-testid="password-gate"
        style={{
          background: "#0f1a2e", border: "1px solid #1e3a5f",
          borderRadius: 16, padding: "48px 40px", width: 380,
          boxShadow: "0 0 40px rgba(6,182,212,0.08)",
          animation: shake ? "shake 0.4s ease" : "none"
        }}
      >
        <style>{`
          @keyframes shake {
            0%,100%{transform:translateX(0)}
            20%,60%{transform:translateX(-8px)}
            40%,80%{transform:translateX(8px)}
          }
        `}</style>
        <div style={{ textAlign: "center", marginBottom: 32 }}>
          <div style={{ fontSize: 40, marginBottom: 12 }}>🔒</div>
          <h1 style={{ color: "#e2e8f0", fontSize: 22, fontWeight: 700, margin: 0 }}>
            Question Bank Manager
          </h1>
          <p style={{ color: "#64748b", fontSize: 13, marginTop: 6 }}>
            Private admin access only
          </p>
        </div>
        <input
          data-testid="password-input"
          type="password"
          placeholder="Enter password"
          value={val}
          onChange={e => { setVal(e.target.value); setErr(false); }}
          onKeyDown={e => e.key === "Enter" && submit()}
          style={{
            width: "100%", background: "#0a1628", border: `1px solid ${err ? "#ef4444" : "#1e3a5f"}`,
            borderRadius: 8, padding: "12px 16px", color: "#e2e8f0",
            fontSize: 15, outline: "none", boxSizing: "border-box",
            transition: "border-color 0.2s"
          }}
        />
        {err && <p style={{ color: "#ef4444", fontSize: 12, marginTop: 6 }}>Incorrect password</p>}
        <button
          data-testid="password-submit"
          onClick={submit}
          style={{
            marginTop: 16, width: "100%", background: "#06b6d4",
            border: "none", borderRadius: 8, padding: "12px",
            color: "#000", fontWeight: 700, fontSize: 15, cursor: "pointer",
            transition: "background 0.2s"
          }}
          onMouseEnter={e => e.target.style.background = "#0891b2"}
          onMouseLeave={e => e.target.style.background = "#06b6d4"}
        >
          Unlock →
        </button>
      </div>
    </div>
  );
}

// ── Stats Panel ───────────────────────────────────────────────────
function StatsPanel({ stats, hierarchy, onRefresh }) {
  const [expanded, setExpanded] = useState({});

  const toggle = (key) => setExpanded(p => ({ ...p, [key]: !p[key] }));

  return (
    <div style={{
      background: "#0d1829", border: "1px solid #1e3a5f",
      borderRadius: 12, padding: 24, height: "100%", overflowY: "auto"
    }}>
      <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 20 }}>
        <h2 style={{ color: "#e2e8f0", fontSize: 16, fontWeight: 700, margin: 0 }}>
          Database Stats
        </h2>
        <button
          data-testid="refresh-stats"
          onClick={onRefresh}
          style={{ background: "transparent", border: "1px solid #1e3a5f", borderRadius: 6,
            color: "#64748b", cursor: "pointer", padding: "4px 10px", fontSize: 12 }}
        >
          ↻ Refresh
        </button>
      </div>

      {stats && (
        <>
          <div style={{
            display: "grid", gridTemplateColumns: "1fr 1fr", gap: 12, marginBottom: 20
          }}>
            <StatCard label="Total Questions" value={stats.total_questions?.toLocaleString()} color="#06b6d4" />
            <StatCard label="Topics" value={stats.total_topics?.toLocaleString()} color="#8b5cf6" />
          </div>

          {/* By Exam Breakdown */}
          <div style={{ marginBottom: 16 }}>
            <p style={{ color: "#64748b", fontSize: 11, textTransform: "uppercase", letterSpacing: 1, marginBottom: 8 }}>
              By Exam
            </p>
            {stats.by_exam?.map(e => (
              <div key={e.exam} style={{ marginBottom: 4 }}>
                <div
                  onClick={() => toggle(e.exam)}
                  style={{
                    display: "flex", justifyContent: "space-between",
                    cursor: "pointer", padding: "6px 8px",
                    borderRadius: 6, background: expanded[e.exam] ? "#1e3a5f22" : "transparent",
                    transition: "background 0.15s"
                  }}
                >
                  <span style={{ color: "#cbd5e1", fontSize: 13 }}>
                    {expanded[e.exam] ? "▼" : "▶"} {e.exam?.toUpperCase()}
                  </span>
                  <span style={{ color: "#06b6d4", fontSize: 13, fontWeight: 600 }}>
                    {e.count?.toLocaleString()}
                  </span>
                </div>

                {expanded[e.exam] && hierarchy?.hierarchy?.[e.exam] && (
                  <div style={{ paddingLeft: 16, borderLeft: "1px solid #1e3a5f", marginLeft: 8, marginTop: 4 }}>
                    {Object.entries(hierarchy.hierarchy[e.exam].subjects || {}).map(([sk, sv]) => (
                      <div key={sk} style={{ marginBottom: 2 }}>
                        <div
                          onClick={() => toggle(`${e.exam}_${sk}`)}
                          style={{
                            display: "flex", justifyContent: "space-between",
                            cursor: "pointer", padding: "4px 6px", borderRadius: 4
                          }}
                        >
                          <span style={{ color: "#94a3b8", fontSize: 12 }}>
                            {expanded[`${e.exam}_${sk}`] ? "▼" : "▶"} {sv.label}
                          </span>
                          <span style={{ color: "#475569", fontSize: 12 }}>
                            {sv.total_questions || 0}
                          </span>
                        </div>
                        {expanded[`${e.exam}_${sk}`] && (
                          <div style={{ paddingLeft: 12, borderLeft: "1px solid #0f2540", marginLeft: 6 }}>
                            {sv.topics?.filter(t => t.question_count > 0).slice(0, 10).map(t => (
                              <div key={t.slug} style={{
                                display: "flex", justifyContent: "space-between",
                                padding: "3px 4px"
                              }}>
                                <span style={{ color: "#64748b", fontSize: 11 }}>{t.name}</span>
                                <span style={{ color: "#334155", fontSize: 11 }}>{t.question_count}</span>
                              </div>
                            ))}
                          </div>
                        )}
                      </div>
                    ))}
                  </div>
                )}
              </div>
            ))}
          </div>

          {/* Difficulty */}
          <div>
            <p style={{ color: "#64748b", fontSize: 11, textTransform: "uppercase", letterSpacing: 1, marginBottom: 8 }}>
              By Difficulty
            </p>
            <div style={{ display: "flex", gap: 8 }}>
              {stats.by_difficulty?.map(d => (
                <DiffBadge key={d.difficulty} label={d.difficulty} count={d.count} />
              ))}
            </div>
          </div>
        </>
      )}

      {!stats && (
        <p style={{ color: "#475569", fontSize: 13, textAlign: "center", marginTop: 40 }}>
          Loading stats...
        </p>
      )}
    </div>
  );
}

function StatCard({ label, value, color }) {
  return (
    <div style={{
      background: "#0a1628", border: "1px solid #1e3a5f",
      borderRadius: 8, padding: "12px 14px"
    }}>
      <p style={{ color: "#64748b", fontSize: 10, margin: 0, textTransform: "uppercase", letterSpacing: 0.8 }}>
        {label}
      </p>
      <p style={{ color: color, fontSize: 24, fontWeight: 700, margin: "4px 0 0" }}>
        {value ?? "—"}
      </p>
    </div>
  );
}

function DiffBadge({ label, count }) {
  const colors = { easy: "#10b981", medium: "#f59e0b", hard: "#ef4444" };
  return (
    <div style={{
      background: `${colors[label] || "#64748b"}15`,
      border: `1px solid ${colors[label] || "#64748b"}40`,
      borderRadius: 6, padding: "4px 10px", textAlign: "center"
    }}>
      <p style={{ color: colors[label] || "#64748b", fontSize: 11, margin: 0, textTransform: "capitalize" }}>{label}</p>
      <p style={{ color: "#e2e8f0", fontSize: 14, fontWeight: 700, margin: 0 }}>{count}</p>
    </div>
  );
}

// ── Campaign Form ─────────────────────────────────────────────────
function CampaignForm({ onStart }) {
  const [exam, setExam] = useState(null);
  const [subjects, setSubjects] = useState([]);
  const [starting, setStarting] = useState(false);
  const [examList, setExamList] = useState([]);
  const [subjectList, setSubjectList] = useState([]);
  const [loadingExams, setLoadingExams] = useState(true);
  const [loadingSubjects, setLoadingSubjects] = useState(false);

  // Fetch exams from LMS (same source as LMS Manager)
  useEffect(() => {
    fetch(`${API}/api/notes/lms/syllabus/exams`)
      .then(r => r.json())
      .then(d => {
        const exams = (d.exams || []).map(e => ({ key: e.id, label: e.name }));
        setExamList(exams);
        if (exams.length > 0) setExam(exams[0].key);
      })
      .catch(() => {})
      .finally(() => setLoadingExams(false));
  }, []);

  // Fetch subjects when exam changes
  useEffect(() => {
    if (!exam) return;
    setLoadingSubjects(true);
    setSubjects([]);
    fetch(`${API}/api/notes/lms/syllabus/exams/${exam}/subjects`)
      .then(r => r.json())
      .then(d => setSubjectList((d.subjects || []).map(s => ({ key: s.id, label: s.name }))))
      .catch(() => setSubjectList([]))
      .finally(() => setLoadingSubjects(false));
  }, [exam]);

  const toggleSubject = (sk) =>
    setSubjects(p => p.includes(sk) ? p.filter(s => s !== sk) : [...p, sk]);

  const handleStart = async () => {
    if (!subjects.length) return;
    setStarting(true);
    const examLabel = examList.find(e => e.key === exam)?.label || exam;
    const subjectLabels = {};
    subjects.forEach(sk => {
      subjectLabels[sk] = subjectList.find(s => s.key === sk)?.label || sk;
    });
    await onStart(exam, subjects, examLabel, subjectLabels);
    setStarting(false);
    setSubjects([]);
  };

  return (
    <div style={{
      background: "#0d1829", border: "1px solid #1e3a5f",
      borderRadius: 12, padding: 24
    }}>
      <h2 style={{ color: "#e2e8f0", fontSize: 16, fontWeight: 700, margin: "0 0 20px" }}>
        New Scrape Campaign
      </h2>

      {/* Exam selector */}
      <label style={{ color: "#64748b", fontSize: 12, display: "block", marginBottom: 6 }}>
        EXAM CATEGORY
      </label>
      <select
        data-testid="exam-select"
        value={exam || ""}
        onChange={e => { setExam(e.target.value); setSubjects([]); }}
        disabled={loadingExams}
        style={{
          width: "100%", background: "#0a1628", border: "1px solid #1e3a5f",
          borderRadius: 8, padding: "10px 12px", color: "#e2e8f0",
          fontSize: 14, marginBottom: 20, cursor: "pointer", outline: "none"
        }}
      >
        {loadingExams && <option>Loading...</option>}
        {!loadingExams && examList.length === 0 && (
          <option disabled>No exams found — add exams in LMS Manager first</option>
        )}
        {examList.map(e => (
          <option key={e.key} value={e.key}>{e.label}</option>
        ))}
      </select>

      {/* Subject checkboxes */}
      <label style={{ color: "#64748b", fontSize: 12, display: "block", marginBottom: 8 }}>
        SUBJECTS <span style={{ color: "#334155" }}>({subjects.length} selected)</span>
      </label>
      {loadingSubjects ? (
        <p style={{ color: "#475569", fontSize: 13, marginBottom: 24 }}>Loading subjects...</p>
      ) : subjectList.length === 0 ? (
        <p style={{ color: "#334155", fontSize: 13, marginBottom: 24 }}>
          No subjects found for this exam. Add subjects in LMS Manager first.
        </p>
      ) : (
        <div style={{
          display: "grid", gridTemplateColumns: "1fr 1fr", gap: 8, marginBottom: 24,
          maxHeight: 220, overflowY: "auto"
        }}>
          {subjectList.map(s => (
            <div
              key={s.key}
              data-testid={`subject-${s.key}`}
              onClick={() => toggleSubject(s.key)}
              style={{
                display: "flex", alignItems: "center", gap: 8,
                background: subjects.includes(s.key) ? "#1e3a5f" : "#0a1628",
                border: `1px solid ${subjects.includes(s.key) ? "#06b6d4" : "#1e3a5f"}`,
                borderRadius: 8, padding: "8px 12px", cursor: "pointer",
                transition: "all 0.15s"
              }}
            >
              <div style={{
                width: 16, height: 16, borderRadius: 4, flexShrink: 0,
                background: subjects.includes(s.key) ? "#06b6d4" : "transparent",
                border: `2px solid ${subjects.includes(s.key) ? "#06b6d4" : "#334155"}`,
                display: "flex", alignItems: "center", justifyContent: "center"
              }}>
                {subjects.includes(s.key) && <span style={{ color: "#000", fontSize: 10, fontWeight: 900 }}>✓</span>}
              </div>
              <span style={{ color: "#e2e8f0", fontSize: 13 }}>{s.label}</span>
            </div>
          ))}
        </div>
      )}

      <button
        data-testid="start-campaign-btn"
        onClick={handleStart}
        disabled={!subjects.length || starting}
        style={{
          width: "100%", padding: "12px",
          background: subjects.length && !starting ? "#06b6d4" : "#1e3a5f",
          border: "none", borderRadius: 8,
          color: subjects.length && !starting ? "#000" : "#475569",
          fontWeight: 700, fontSize: 15, cursor: subjects.length && !starting ? "pointer" : "not-allowed",
          transition: "all 0.2s"
        }}
      >
        {starting ? "Starting..." : subjects.length
          ? `Start Scraping ${subjects.length} Subject${subjects.length > 1 ? "s" : ""}`
          : "Select at least one subject"}
      </button>

      <p style={{ color: "#334155", fontSize: 11, marginTop: 10, textAlign: "center" }}>
        Scrapes previous year questions from internet. AI rewrites in bilingual format.
      </p>
    </div>
  );
}

// ── Jobs Tracker ──────────────────────────────────────────────────
function JobsTracker({ campaigns, onControl, onControlJob }) {
  if (!campaigns.length) {
    return (
      <div style={{
        background: "#0d1829", border: "1px solid #1e3a5f",
        borderRadius: 12, padding: 32, textAlign: "center"
      }}>
        <p style={{ color: "#334155", fontSize: 14 }}>No active jobs yet</p>
        <p style={{ color: "#1e3a5f", fontSize: 12 }}>Start a campaign above to begin scraping</p>
      </div>
    );
  }

  return (
    <div style={{ display: "flex", flexDirection: "column", gap: 12 }}>
      {campaigns.map(c => <JobCard key={c.campaign_id} job={c} onControl={onControl} />)}
    </div>
  );
}

function JobCard({ job, onControl }) {
  const progress = pct(job.completed_urls, job.total_urls);
  const sColor = statusColor(job.status);

  return (
    <div
      data-testid={`job-${job.campaign_id}`}
      style={{
        background: "#0d1829",
        borderTop: `1px solid ${sColor}30`,
        borderRight: `1px solid ${sColor}30`,
        borderBottom: `1px solid ${sColor}30`,
        borderLeft: `3px solid ${sColor}`,
        borderRadius: 12, padding: 18,
      }}
    >
      <div style={{ display: "flex", justifyContent: "space-between", alignItems: "flex-start", marginBottom: 10 }}>
        <div>
          <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
            <span style={{ color: "#e2e8f0", fontSize: 14, fontWeight: 600 }}>
              {job.exam_type?.toUpperCase()} Campaign
            </span>
            <span style={{
              background: `${sColor}20`, color: sColor,
              fontSize: 10, padding: "2px 8px", borderRadius: 20, fontWeight: 600,
              textTransform: "uppercase", letterSpacing: 0.5
            }}>
              {job.status}
            </span>
          </div>
          <p style={{ color: "#475569", fontSize: 12, margin: "3px 0 0" }}>
            Subjects: {job.subjects?.map(s =>
              job.by_subject?.[s]?.label || s
            ).join(", ")}
          </p>
        </div>
        <div style={{ display: "flex", gap: 6 }}>
          {job.status === "running" && (
            <CtrlBtn label="Pause" color="#f59e0b"
              onClick={() => onControl(job.campaign_id, "pause")} />
          )}
          {job.status === "paused" && (
            <CtrlBtn label="Resume" color="#06b6d4"
              onClick={() => onControl(job.campaign_id, "resume")} />
          )}
          {["running", "paused", "queued"].includes(job.status) && (
            <CtrlBtn label="Stop" color="#ef4444"
              onClick={() => onControl(job.campaign_id, "stop")} />
          )}
        </div>
      </div>

      {/* Progress bar */}
      {job.total_urls > 0 && (
        <div style={{ marginBottom: 10 }}>
          <div style={{
            background: "#0a1628", borderRadius: 4, height: 6, overflow: "hidden"
          }}>
            <div style={{
              width: `${progress}%`, height: "100%", background: sColor,
              transition: "width 0.5s ease",
              borderRadius: 4
            }} />
          </div>
          <div style={{ display: "flex", justifyContent: "space-between", marginTop: 4 }}>
            <span style={{ color: "#64748b", fontSize: 11 }}>{progress}% complete</span>
            <span style={{ color: "#64748b", fontSize: 11 }}>
              {job.completed_urls}/{job.total_urls} URLs
            </span>
          </div>
        </div>
      )}

      {/* Stats row */}
      <div style={{ display: "flex", gap: 16, marginBottom: 8 }}>
        <span style={{ color: "#10b981", fontSize: 13, fontWeight: 600 }}>
          ✓ {job.total_questions_saved || 0} questions saved
        </span>
        {job.current_subject && job.status === "running" && (
          <span style={{ color: "#64748b", fontSize: 12 }}>
            → {job.by_subject?.[job.current_subject]?.label || job.current_subject}
          </span>
        )}
      </div>

      {/* Message */}
      {job.message && (
        <p style={{
          color: "#475569", fontSize: 11, margin: 0,
          fontFamily: "monospace", wordBreak: "break-all"
        }}>
          {job.message}
        </p>
      )}

      {/* Per-subject breakdown */}
      {job.by_subject && Object.keys(job.by_subject).length > 0 && (
        <div style={{ marginTop: 12, display: "flex", flexWrap: "wrap", gap: 6 }}>
          {Object.entries(job.by_subject).map(([sk, sv]) => (
            <div key={sk} style={{
              background: "#0a1628", border: "1px solid #1e3a5f",
              borderRadius: 6, padding: "4px 10px", fontSize: 11
            }}>
              <span style={{ color: "#94a3b8" }}>{sv.label || sk}: </span>
              <span style={{ color: "#06b6d4", fontWeight: 600 }}>{sv.questions_saved || 0} Q</span>
              {sv.urls_success > 0 && (
                <span style={{ color: "#10b981" }}> ({sv.urls_success}/{sv.urls_tried} URLs)</span>
              )}
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

function CtrlBtn({ label, color, onClick }) {
  return (
    <button
      data-testid={`ctrl-${label.toLowerCase()}`}
      onClick={onClick}
      style={{
        background: `${color}15`, border: `1px solid ${color}40`,
        color: color, borderRadius: 6, padding: "4px 12px", cursor: "pointer",
        fontSize: 12, fontWeight: 600, transition: "all 0.15s"
      }}
      onMouseEnter={e => { e.target.style.background = `${color}30`; }}
      onMouseLeave={e => { e.target.style.background = `${color}15`; }}
    >
      {label}
    </button>
  );
}

// ── Questions Browser ─────────────────────────────────────────────
function QuestionsBrowser({ lmsExams }) {
  const [selectedLang, setSelectedLang] = useState({ code: "original", name: "Original", native: "Original" });
  const [languages, setLanguages] = useState([]);
  const [questions, setQuestions] = useState([]);
  const [total, setTotal] = useState(0);
  const [pages, setPages] = useState(1);
  const [page, setPage] = useState(1);
  const [filterExam, setFilterExam] = useState("");
  const [filterSubject, setFilterSubject] = useState("");
  const [subjectList, setSubjectList] = useState([]);
  const [translations, setTranslations] = useState({}); // { `${qid}_${langCode}`: translated }
  const [loadingTrans, setLoadingTrans] = useState({}); // { `${qid}_${langCode}`: true }
  const [loadingQ, setLoadingQ] = useState(false);

  // Fetch languages
  useEffect(() => {
    fetch(`${API}/api/question-bank/languages`)
      .then(r => r.json())
      .then(d => setLanguages(d.languages || []));
  }, []);

  // Fetch subjects when exam changes
  useEffect(() => {
    if (!filterExam) { setSubjectList([]); setFilterSubject(""); return; }
    fetch(`${API}/api/notes/lms/syllabus/exams/${filterExam}/subjects`)
      .then(r => r.json())
      .then(d => setSubjectList(d.subjects || []))
      .catch(() => setSubjectList([]));
    setFilterSubject("");
  }, [filterExam]);

  const fetchQuestions = useCallback(async (p = 1) => {
    setLoadingQ(true);
    try {
      let url = `${API}/api/question-bank/questions/browse?page=${p}&limit=20`;
      if (filterExam)   url += `&exam_type=${filterExam}`;
      if (filterSubject) url += `&subject=${filterSubject}`;
      const r = await fetch(url);
      const d = await r.json();
      setQuestions(d.questions || []);
      setTotal(d.total || 0);
      setPages(d.pages || 1);
      setPage(p);
    } finally { setLoadingQ(false); }
  }, [filterExam, filterSubject]);

  useEffect(() => { fetchQuestions(1); }, [fetchQuestions]);

  const translate = async (qid, lang) => {
    const key = `${qid}_${lang.code}`;
    if (translations[key] || loadingTrans[key]) return;
    setLoadingTrans(p => ({ ...p, [key]: true }));
    try {
      const r = await fetch(`${API}/api/question-bank/translate`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ question_id: qid, lang_code: lang.code, lang_name: lang.name })
      });
      const d = await r.json();
      if (d.success) {
        setTranslations(p => ({ ...p, [key]: d }));
      }
    } catch (e) { console.error("Translation failed", e); }
    finally { setLoadingTrans(p => ({ ...p, [key]: false })); }
  };

  const handleLangClick = (lang) => {
    setSelectedLang(lang);
    // Pre-translate all visible questions for this language
    if (lang.code !== "original") {
      questions.forEach(q => translate(q.id, lang));
    }
  };

  const getDisplayQ = (q) => {
    if (selectedLang.code === "original") return q;
    const key = `${q.id}_${selectedLang.code}`;
    const tr = translations[key];
    if (tr) return { ...q, question_text: tr.question_text, options: tr.options,
                     correct_answer: tr.answer, explanation: tr.explanation };
    return q;
  };

  const allLangs = [{ code: "original", name: "Original", native: "Original" }, ...languages];

  return (
    <div style={{ display: "flex", gap: 0, height: "calc(100vh - 130px)", overflow: "hidden" }}>
      {/* ── Left: Language Sidebar ── */}
      <div style={{
        width: 160, flexShrink: 0, background: "#0a1220",
        borderRight: "1px solid #1e3a5f", overflowY: "auto", paddingTop: 8
      }}>
        <p style={{ color: "#334155", fontSize: 10, textTransform: "uppercase",
                    letterSpacing: 1, padding: "8px 12px 4px" }}>
          Language
        </p>
        {allLangs.map(lang => {
          const isActive = selectedLang.code === lang.code;
          const transCount = questions.filter(q => translations[`${q.id}_${lang.code}`]).length;
          return (
            <div
              key={lang.code}
              data-testid={`lang-${lang.code}`}
              onClick={() => handleLangClick(lang)}
              style={{
                padding: "9px 12px", cursor: "pointer", borderLeft: `3px solid ${isActive ? "#06b6d4" : "transparent"}`,
                background: isActive ? "#0d1e35" : "transparent",
                transition: "all 0.12s"
              }}
            >
              <div style={{ color: isActive ? "#06b6d4" : "#94a3b8", fontSize: 12, fontWeight: isActive ? 700 : 400 }}>
                {lang.native}
              </div>
              {lang.code !== "original" && transCount > 0 && (
                <div style={{ color: "#10b98160", fontSize: 9, marginTop: 1 }}>
                  {transCount} cached
                </div>
              )}
            </div>
          );
        })}
      </div>

      {/* ── Right: Questions ── */}
      <div style={{ flex: 1, display: "flex", flexDirection: "column", overflow: "hidden" }}>
        {/* Filters */}
        <div style={{
          background: "#0d1829", borderBottom: "1px solid #1e3a5f",
          padding: "10px 16px", display: "flex", gap: 12, alignItems: "center", flexWrap: "wrap"
        }}>
          <select
            data-testid="filter-exam"
            value={filterExam}
            onChange={e => setFilterExam(e.target.value)}
            style={selectStyle}
          >
            <option value="">All Exams</option>
            {lmsExams.map(e => <option key={e.key} value={e.key}>{e.label}</option>)}
          </select>

          <select
            data-testid="filter-subject"
            value={filterSubject}
            onChange={e => setFilterSubject(e.target.value)}
            disabled={!filterExam}
            style={{ ...selectStyle, opacity: filterExam ? 1 : 0.4 }}
          >
            <option value="">All Subjects</option>
            {subjectList.map(s => <option key={s.id} value={s.id}>{s.name}</option>)}
          </select>

          <span style={{ color: "#475569", fontSize: 12, marginLeft: "auto" }}>
            <span style={{ color: "#06b6d4", fontWeight: 700 }}>{total.toLocaleString()}</span> questions
            {selectedLang.code !== "original" && (
              <span style={{ color: "#f59e0b" }}> · Viewing in {selectedLang.native}</span>
            )}
          </span>
        </div>

        {/* Questions list */}
        <div style={{ flex: 1, overflowY: "auto", padding: 16 }}>
          {loadingQ && (
            <div style={{ textAlign: "center", color: "#475569", marginTop: 60 }}>Loading questions...</div>
          )}
          {!loadingQ && questions.length === 0 && (
            <div style={{ textAlign: "center", color: "#334155", marginTop: 60 }}>
              <p style={{ fontSize: 16 }}>No questions found</p>
              <p style={{ fontSize: 13, marginTop: 8 }}>Start a scraping campaign to populate the question bank</p>
            </div>
          )}
          {!loadingQ && questions.map((q, i) => {
            const dq = getDisplayQ(q);
            const transKey = `${q.id}_${selectedLang.code}`;
            const isTranslating = loadingTrans[transKey];
            const isCached = translations[transKey]?.cached;
            return (
              <div key={q.id} style={{
                background: "#0d1829", border: "1px solid #1e3a5f",
                borderRadius: 10, padding: 16, marginBottom: 12
              }}>
                <div style={{ display: "flex", justifyContent: "space-between", marginBottom: 8 }}>
                  <span style={{ color: "#475569", fontSize: 11 }}>
                    Q{((page - 1) * 20) + i + 1}
                    {q.topic_name && <span> · {q.topic_name}</span>}
                    {q.subject && <span> · {q.subject?.toUpperCase()}</span>}
                  </span>
                  <div style={{ display: "flex", gap: 6, alignItems: "center" }}>
                    {isTranslating && (
                      <span style={{ color: "#f59e0b", fontSize: 10 }}>translating...</span>
                    )}
                    {!isTranslating && selectedLang.code !== "original" && translations[transKey] && (
                      <span style={{
                        background: isCached ? "#10b98120" : "#06b6d420",
                        color: isCached ? "#10b981" : "#06b6d4",
                        fontSize: 9, padding: "2px 6px", borderRadius: 10
                      }}>
                        {isCached ? "cached" : "translated"}
                      </span>
                    )}
                    {q.difficulty && (
                      <span style={{
                        fontSize: 9, padding: "2px 7px", borderRadius: 10,
                        background: q.difficulty === "easy" ? "#10b98120" : q.difficulty === "hard" ? "#ef444420" : "#f59e0b20",
                        color: q.difficulty === "easy" ? "#10b981" : q.difficulty === "hard" ? "#ef4444" : "#f59e0b"
                      }}>{q.difficulty}</span>
                    )}
                  </div>
                </div>

                <p style={{ color: "#e2e8f0", fontSize: 14, lineHeight: 1.6, margin: "0 0 12px" }}>
                  {dq.question_text}
                </p>

                {dq.options && typeof dq.options === "object" && Object.keys(dq.options).length > 0 && (
                  <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 6 }}>
                    {Object.entries(dq.options).map(([k, v]) => {
                      const isCorrect = k === (dq.correct_answer || q.correct_answer);
                      return (
                        <div key={k} style={{
                          background: isCorrect ? "#10b98115" : "#0a1628",
                          border: `1px solid ${isCorrect ? "#10b98140" : "#1e3a5f"}`,
                          borderRadius: 6, padding: "6px 10px",
                          display: "flex", gap: 8, alignItems: "flex-start"
                        }}>
                          <span style={{ color: isCorrect ? "#10b981" : "#475569", fontWeight: 700, fontSize: 12, flexShrink: 0 }}>
                            {k})
                          </span>
                          <span style={{ color: isCorrect ? "#10b981" : "#94a3b8", fontSize: 13 }}>{v}</span>
                        </div>
                      );
                    })}
                  </div>
                )}

                {dq.explanation && (
                  <p style={{ color: "#64748b", fontSize: 12, marginTop: 10, paddingTop: 8,
                               borderTop: "1px solid #1e3a5f20", fontStyle: "italic" }}>
                    {dq.explanation}
                  </p>
                )}
              </div>
            );
          })}

          {/* Pagination */}
          {pages > 1 && (
            <div style={{ display: "flex", justifyContent: "center", gap: 8, padding: "16px 0" }}>
              <PaginBtn label="← Prev" disabled={page <= 1} onClick={() => fetchQuestions(page - 1)} />
              <span style={{ color: "#475569", fontSize: 13, alignSelf: "center" }}>
                {page} / {pages}
              </span>
              <PaginBtn label="Next →" disabled={page >= pages} onClick={() => fetchQuestions(page + 1)} />
            </div>
          )}
        </div>
      </div>
    </div>
  );
}

const selectStyle = {
  background: "#0a1628", border: "1px solid #1e3a5f", borderRadius: 7,
  padding: "7px 10px", color: "#e2e8f0", fontSize: 13, cursor: "pointer", outline: "none"
};

function PaginBtn({ label, disabled, onClick }) {
  return (
    <button onClick={onClick} disabled={disabled} style={{
      background: disabled ? "#0a1628" : "#1e3a5f", border: "1px solid #1e3a5f",
      color: disabled ? "#334155" : "#94a3b8", borderRadius: 6, padding: "6px 14px",
      cursor: disabled ? "not-allowed" : "pointer", fontSize: 13
    }}>{label}</button>
  );
}

// ── Main Page ─────────────────────────────────────────────────────
export default function QuestionBankAdmin() {
  const [authed, setAuthed] = useState(() =>
    sessionStorage.getItem("qb_admin_authed") === "1"
  );
  const [activeTab, setActiveTab] = useState("jobs"); // "jobs" | "questions"
  const [hierarchy, setHierarchy] = useState(null);
  const [stats, setStats] = useState(null);
  const [campaigns, setCampaigns] = useState([]);
  const [lmsExams, setLmsExams] = useState([]);
  const pollRef = useRef(null);

  const fetchHierarchy = useCallback(async () => {
    try {
      const r = await fetch(`${API}/api/question-bank/hierarchy`);
      const d = await r.json();
      setHierarchy(d);
    } catch (e) { console.error("Hierarchy fetch failed", e); }
  }, []);

  const fetchStats = useCallback(async () => {
    try {
      const r = await fetch(`${API}/api/question-bank/admin/stats`);
      const d = await r.json();
      setStats(d);
    } catch (e) { console.error("Stats fetch failed", e); }
  }, []);

  const fetchCampaigns = useCallback(async () => {
    try {
      const r = await fetch(`${API}/api/question-bank/campaigns`);
      const d = await r.json();
      if (d.success) setCampaigns(d.campaigns || []);
    } catch (e) { console.error("Campaigns fetch failed", e); }
  }, []);

  // Fetch LMS exams for Questions browser filter
  const fetchLmsExams = useCallback(async () => {
    try {
      const r = await fetch(`${API}/api/notes/lms/syllabus/exams`);
      const d = await r.json();
      setLmsExams((d.exams || []).map(e => ({ key: e.id, label: e.name })));
    } catch (e) { console.error("LMS exams fetch failed", e); }
  }, []);

  useEffect(() => {
    if (!authed) return;
    fetchHierarchy();
    fetchStats();
    fetchCampaigns();
    fetchLmsExams();
    pollRef.current = setInterval(() => { fetchCampaigns(); }, 3000);
    return () => clearInterval(pollRef.current);
  }, [authed, fetchHierarchy, fetchStats, fetchCampaigns, fetchLmsExams]);

  const handleStart = async (exam, subjects, examLabel, subjectLabels) => {
    try {
      const r = await fetch(`${API}/api/question-bank/campaign`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ exam_type: exam, subjects, exam_label: examLabel, subject_labels: subjectLabels })
      });
      const d = await r.json();
      if (d.success) { await fetchCampaigns(); setActiveTab("jobs"); }
    } catch (e) { console.error("Start campaign failed", e); }
  };

  const handleControl = async (campaign_id, action) => {
    try {
      await fetch(`${API}/api/question-bank/campaign/${campaign_id}/control`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ action })
      });
      await fetchCampaigns();
    } catch (e) { console.error("Control failed", e); }
  };

  if (!authed) return <PasswordGate onAuth={() => setAuthed(true)} />;

  const activeCount = campaigns.filter(c => ["running", "paused", "queued"].includes(c.status)).length;

  return (
    <div style={{ minHeight: "100vh", background: "#080e1a", fontFamily: "system-ui, sans-serif" }}>
      {/* Header */}
      <div style={{
        background: "#0d1829", borderBottom: "1px solid #1e3a5f",
        padding: "12px 24px", display: "flex", justifyContent: "space-between", alignItems: "center"
      }}>
        <div style={{ display: "flex", alignItems: "center", gap: 20 }}>
          <h1 style={{ color: "#06b6d4", fontSize: 18, fontWeight: 800, margin: 0 }}>
            Question Bank Manager
          </h1>

          {/* Tab navigation */}
          <div style={{ display: "flex", gap: 4, background: "#0a1628", borderRadius: 8, padding: 3 }}>
            {[
              { id: "jobs", label: "Scraping Jobs" },
              { id: "questions", label: `Questions ${stats ? `(${stats.total_questions?.toLocaleString()})` : ""}` },
            ].map(tab => (
              <button
                key={tab.id}
                data-testid={`tab-${tab.id}`}
                onClick={() => setActiveTab(tab.id)}
                style={{
                  background: activeTab === tab.id ? "#1e3a5f" : "transparent",
                  border: "none", borderRadius: 6, padding: "6px 14px",
                  color: activeTab === tab.id ? "#e2e8f0" : "#64748b",
                  fontSize: 13, fontWeight: activeTab === tab.id ? 600 : 400, cursor: "pointer",
                  transition: "all 0.15s"
                }}
              >
                {tab.label}
              </button>
            ))}
          </div>

          {activeCount > 0 && (
            <span style={{
              background: "#06b6d420", border: "1px solid #06b6d440",
              color: "#06b6d4", borderRadius: 20, padding: "2px 10px", fontSize: 11, fontWeight: 600
            }}>
              {activeCount} running
            </span>
          )}
        </div>

        <button
          data-testid="logout-btn"
          onClick={() => { sessionStorage.removeItem("qb_admin_authed"); setAuthed(false); }}
          style={{
            background: "transparent", border: "1px solid #334155",
            borderRadius: 6, color: "#64748b", cursor: "pointer", padding: "6px 14px", fontSize: 13
          }}
        >
          Logout
        </button>
      </div>

      {/* Tab content */}
      {activeTab === "jobs" && (
        <div style={{ display: "grid", gridTemplateColumns: "300px 1fr", gap: 20, padding: 20 }}>
          {/* Left: Stats */}
          <StatsPanel stats={stats} hierarchy={hierarchy} onRefresh={() => { fetchStats(); fetchHierarchy(); }} />

          {/* Right: Campaign form + jobs */}
          <div style={{ display: "flex", flexDirection: "column", gap: 18 }}>
            <CampaignForm onStart={handleStart} />
            <div style={{ display: "flex", alignItems: "center", gap: 10 }}>
              <h2 style={{ color: "#94a3b8", fontSize: 14, fontWeight: 600, margin: 0 }}>
                Scraping Jobs
              </h2>
              {campaigns.length > 0 && (
                <span style={{ color: "#475569", fontSize: 12 }}>
                  ({campaigns.length} total)
                </span>
              )}
            </div>
            <JobsTracker campaigns={campaigns} onControl={handleControl} />
          </div>
        </div>
      )}

      {activeTab === "questions" && (
        <QuestionsBrowser lmsExams={lmsExams} />
      )}
    </div>
  );
}
